// Authored ordered-sequence banks — the data behind the `order` exercise in
// `sequence` mode ("put these in the correct order"). Tiles are stored in the
// CORRECT order; the engine shuffles them for display and grades on the
// concatenation, so exactly one arrangement is right (all tiles are distinct
// surfaces, so the order is uniquely recoverable). These drill fixed recitation
// orders the lessons teach: the vowel garland, a varga row, the five places,
// and the canonical paradigm order.

import type { SequenceItem } from './types';

// --- reading: vowel + consonant recitation orders (Reading L2/L3) ------------
const READING_SEQUENCES: SequenceItem[] = [
  {
    id: 'seq-vowels-primary', bucket: 'vowels',
    tiles: [
      { deva: 'अ', iast: 'a' }, { deva: 'आ', iast: 'ā' }, { deva: 'इ', iast: 'i' },
      { deva: 'ई', iast: 'ī' }, { deva: 'उ', iast: 'u' }, { deva: 'ऊ', iast: 'ū' },
    ],
    instruction: 'Put the six primary vowels in the traditional recitation order.',
    gloss: 'a ā i ī u ū — the order of every Sanskrit dictionary and paradigm.',
  },
  {
    id: 'seq-vowels-diphthong', bucket: 'vowels',
    tiles: [
      { deva: 'ए', iast: 'e' }, { deva: 'ऐ', iast: 'ai' }, { deva: 'ओ', iast: 'o' }, { deva: 'औ', iast: 'au' },
    ],
    instruction: 'Put the four diphthong-class vowels in order.',
    gloss: 'e ai o au — the diphthong class, all long.',
  },
  {
    id: 'seq-velar-row', bucket: 'varga',
    tiles: [
      { deva: 'क', iast: 'ka' }, { deva: 'ख', iast: 'kha' }, { deva: 'ग', iast: 'ga' },
      { deva: 'घ', iast: 'gha' }, { deva: 'ङ', iast: 'ṅa' },
    ],
    instruction: 'Order the velar row by manner: unvoiced, unvoiced-aspirated, voiced, voiced-aspirated, nasal.',
    gloss: 'ka kha ga gha ṅa — the five manners across one varga.',
  },
  {
    id: 'seq-palatal-row', bucket: 'varga',
    tiles: [
      { deva: 'च', iast: 'ca' }, { deva: 'छ', iast: 'cha' }, { deva: 'ज', iast: 'ja' },
      { deva: 'झ', iast: 'jha' }, { deva: 'ञ', iast: 'ña' },
    ],
    instruction: 'Order the palatal row by manner: unvoiced, unvoiced-aspirated, voiced, voiced-aspirated, nasal.',
    gloss: 'ca cha ja jha ña — the palatal varga.',
  },
  {
    id: 'seq-varga-places', bucket: 'varga',
    tiles: [
      { deva: 'क', iast: 'ka' }, { deva: 'च', iast: 'ca' }, { deva: 'ट', iast: 'ṭa' },
      { deva: 'त', iast: 'ta' }, { deva: 'प', iast: 'pa' },
    ],
    instruction: 'Order the five varga leaders by place, from the back of the mouth to the lips.',
    gloss: 'ka (velar) · ca (palatal) · ṭa (retroflex) · ta (dental) · pa (labial).',
  },
];

// --- grammar: the canonical paradigm order (Grammar L4–L8) -------------------
const GRAMMAR_SEQUENCES: SequenceItem[] = [
  {
    id: 'seq-cases-deva', bucket: 'paradigm-order',
    tiles: [
      { deva: 'देवः', iast: 'devaḥ' }, { deva: 'देवम्', iast: 'devam' }, { deva: 'देवेन', iast: 'devena' },
      { deva: 'देवाय', iast: 'devāya' }, { deva: 'देवात्', iast: 'devāt' }, { deva: 'देवस्य', iast: 'devasya' },
      { deva: 'देवे', iast: 'deve' }, { deva: 'देव', iast: 'deva' },
    ],
    instruction: 'Put the देव singular in the fixed paradigm order — case 1 (nominative) through case 8 (vocative).',
    gloss: 'devaḥ · devam · devena · devāya · devāt · devasya · deve · (he) deva.',
  },
  {
    id: 'seq-verb-sg', bucket: 'paradigm-order',
    tiles: [
      { deva: 'भवामि', iast: 'bhavāmi' }, { deva: 'भवसि', iast: 'bhavasi' }, { deva: 'भवति', iast: 'bhavati' },
    ],
    instruction: 'Put the present singular of √bhū in the order used in this course — 1st, 2nd, 3rd person.',
    gloss: 'bhavāmi (I) · bhavasi (you) · bhavati (he/she/it).',
  },
];

const SEQUENCE_SETS: Record<string, SequenceItem[]> = {
  'reading-sequences': READING_SEQUENCES,
  'vowel-order': READING_SEQUENCES.filter((s) => s.bucket === 'vowels'),
  'varga-order': READING_SEQUENCES.filter((s) => s.bucket === 'varga'),
  'grammar-sequences': GRAMMAR_SEQUENCES,
  'paradigm-order': GRAMMAR_SEQUENCES,
};

export function sequenceItems(key: string): SequenceItem[] {
  return SEQUENCE_SETS[key] ?? [];
}
